import { useState, useEffect } from 'react';
import { View, StyleSheet, Dimensions, Platform } from 'react-native';
import { Surface, Text, useTheme, Card, Appbar } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'react-native';
import { useRouter } from 'expo-router';
import { Sidebar, SIDEBAR_WIDTH } from '../../src/components/Sidebar';
import { useUser } from '../../src/hooks/useUser';
import { useProfile } from '../../src/hooks/useProfile';
import { getCompanyDriverLocations } from '../../src/api/apiService';
import MapView from '../../src/components/mymap';
import { LocationRegion } from '../../src/lib/types';
import Logo from '../../src/components/Logo';

// Get screen dimensions dynamically
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Default location for Lisbon
const DEFAULT_LOCATION: LocationRegion = {
  latitude: 38.7223,
  longitude: -9.1393,
  latitudeDelta: 0.0922,
  longitudeDelta: 0.0421,
};

interface DriverLocation {
  id: number;
  name: string;
  location: {
    latitude: number;
    longitude: number;
  };
  isDriving: boolean;
  lastUpdate: string;
}

export default function DriversLocationScreen() {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const userInterfaceStyle = colorScheme === 'dark' ? 'dark' : 'light';
  const isLargeScreen = SCREEN_WIDTH > 1024;
  const { userData, handleLogout } = useUser();
  const router = useRouter();
  
  // Use the new profile hook
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch driver locations
  const fetchDriverLocations = async () => {
    try {
      setLoading(true);
      const response = await getCompanyDriverLocations();
      if (response.ok) {
        setDriverLocations(response.data);
      }
    } catch (error) {
      console.error('Error fetching driver locations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverLocations();
  }, []);

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.container}>
      {isLargeScreen && (
        <Sidebar
          isOpen={true}
          onClose={() => {}} // Not needed for large screens
          screenWidth={SCREEN_WIDTH}
          onLogout={handleLogout}
          isOwner={hasOwner}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
          onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
      
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(isLargeScreen ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[
          styles.contentContainer, 
          !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }
        ]}>
          <Card style={styles.card}>
            <Card.Content>
              <Text variant="headlineSmall" style={{ color: theme.colors.primary, marginBottom: 8 }}>
                {t('ownerDashboard.driverLocations', 'Driver Locations')}
              </Text>
              <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 16 }}>
                {t('ownerDashboard.driverLocationsDesc', 'Real-time location of your drivers')}
              </Text>
              
              {/* Map Container */}
              <View style={styles.mapContainer}>
                {loading ? (
                  <View style={styles.loadingContainer}>
                    <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('common.loading', 'Loading...')}
                    </Text>
                  </View>
                ) : (
                  <MapView
                    style={styles.map}
                    provider={Platform.OS === 'android' ? 'google' : undefined}
                    region={DEFAULT_LOCATION}
                    showsUserLocation={false}
                    userInterfaceStyle={userInterfaceStyle}
                    showsCompass={false}
                    showsMyLocationButton={false}
                    // @ts-ignore markers prop provided by custom MapView wrapper
                    markers={driverLocations as any}
                    children={null}
                  />
                )}
              </View>
              
              {/* Driver Status Summary */}
              <View style={styles.summaryContainer}>
                <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
                  {t('ownerDashboard.driverStatus', 'Driver Status')}
                </Text>
                <View style={styles.statusRow}>
                  <View style={styles.statusItem}>
                    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.totalDrivers', 'Total Drivers')}
                    </Text>
                    <Text variant="headlineSmall" style={{ color: theme.colors.primary }}>
                      {driverLocations.length}
                    </Text>
                  </View>
                  <View style={styles.statusItem}>
                    <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.currentlyDriving', 'Currently Driving')}
                    </Text>
                    <Text variant="headlineSmall" style={{ color: theme.colors.primary }}>
                      {driverLocations.filter(d => d.isDriving).length}
                    </Text>
                  </View>
                </View>
              </View>
            </Card.Content>
          </Card>
        </View>

        {!isLargeScreen && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            screenWidth={SCREEN_WIDTH}
            onLogout={handleLogout}
            isOwner={hasOwner}
            isDriver={hasDriver}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasDriver && hasOwner ? (newProfile) => updateProfile(newProfile) : undefined}
            onRegisterDriver={hasOwner && !hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
          />
        )}
      </Surface>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  contentContainer: {
    flex: 1,
    padding: 20,
  },
  card: {
    flex: 1,
    elevation: 4,
  },
  mapContainer: {
    height: '70%',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
  },
  map: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  summaryContainer: {
    marginTop: 16,
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: 8,
  },
  statusItem: {
    alignItems: 'center',
  },
});

